import React, { useContext, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, CustomInput, Modal, ModalBody, ModalProps, UncontrolledTooltip, ModalHeader, SpinningButton } from "Components/ReactstrapSlim";
import * as Icons from "Components/Icons";
import { InputFL } from "Components/Inputs";
import { Review } from "Components/Review/Review";
import { ITeamMemberReservation, IRatingUpsertModel, RatingClient, RatingUpsertModel, RatingViewModel } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { formatReservationTime } from "Utils/datetime";
import UserContext from "../../UserContext";
import moment from "moment";
import nameof from "Utils/nameof";

interface ReviewModalProps extends ModalProps {
   reservation: ITeamMemberReservation,
   onClosePopup: () => void,
   onLeaveReview: () => void
}

const ratingSystem = [
   { grade: 1, text: "Bad" },
   { grade: 2, text: "So-so" },
   { grade: 3, text: "Normal" },
   { grade: 4, text: "Good" },
   { grade: 5, text: "Great" },
];

function getReviewModel(viewModel: IRatingUpsertModel) {
   return {
      id: viewModel.id,
      memberId: viewModel.memberId,
      name: viewModel.name,
      reservationId: viewModel.reservationId,
      stars: viewModel.stars,
      text: viewModel.text,
      venueId: viewModel.venueId,
      workspaceId: viewModel.workspaceId,
      messageToSupport: viewModel.messageToSupport
   };
}

type ReviewModel = ReturnType<typeof getReviewModel>;

const ratingViewModalSchema: yup.SchemaOf<ReviewModel> = yup.object({
   id: yup.string().optional(),
   memberId: yup.string().required().defined(),
   name: yup.string().required().defined(),
   reservationId: yup.string().required().defined(),
   stars: yup.number().required().max(5).min(1).defined(),
   text: yup.string().optional(),
   venueId: yup.string().required().defined(),
   workspaceId: yup.string().required().defined(),
   messageToSupport: yup.string().trim().when(nameof<ReviewModel>("stars"), {
      is: (stars: number) => stars < 3,
      then: x => x.required("Message to support is a required field"),
      otherwise: x => x.optional()
   })
});

const ReviewModal: React.FC<ReviewModalProps> = function ({ reservation, onClosePopup, onLeaveReview, ...modalProps }) {
   const [hoveredGrade, setHoveredGrade] = useState(0);
   const [isLoading, setIsLoading] = useState(false);
   const [previewModel, setPreviewModel] = useState<RatingViewModel | undefined>(undefined)
   const [step, setStep] = useState(1);

   const { user } = useContext(UserContext);
   const ratingClient = useApiClient(RatingClient);

   const formMethods = useForm<ReviewModel>({
      resolver: yupResolver(ratingViewModalSchema),
      mode: "onTouched",
      defaultValues: {
         memberId: user.id,
         name: reservation.memberReviewName ?? user.fullName,
         reservationId: reservation.idForLink,
         venueId: reservation.venueId,
         workspaceId: reservation.workspaceId,
         stars: reservation.memberStars ?? 0,
         text: reservation.memberReviewText ?? undefined,
         id: reservation.reviewId ?? undefined
      }
   });

   const ratingValues = formMethods.watch();

   const save = async () => {
      const isValid = await formMethods.trigger();
      if (isValid) {
         setIsLoading(true);
         const data = formMethods.getValues();
         try {
            const result = await ratingClient.save(RatingUpsertModel.fromJS(data));
            if (result.success) {
               await onLeaveReview();
               const review = await ratingClient.ownByReservationId(reservation.idForLink);
               setPreviewModel(review);
               moveToStep(2);
            }
         } catch (err) {

         }
      }
      setIsLoading(false);
   }

   const isBtnDisabled = () => ratingValues.stars < 1;

   const moveToStep = (stepNumber: number) => setStep(stepNumber);

   useEffect(() => {
      if (ratingValues.stars > 2) {
         formMethods.setValue("messageToSupport", undefined)
      }
   }, [ratingValues.stars]);

   const reviewerNameOptions = useMemo(() => [{
      id: "full-name",
      value: user.fullName
   }, {
      id: "short-name",
      value: `${user.firstName} ${user.lastName?.substring(0, 1)}.`
   }, {
      id: "anonymous",
      value: "Anonymous"
   }], [user]);

   return (<Modal className="review-modal" toggle={() => onClosePopup()} isOpen={true} size="lg" {...modalProps}>
      <ModalHeader className="review-modal-header" toggle={() => onClosePopup()}>{step == 1 ? "Leave a review about your visit" : "Thank you for review!"}</ModalHeader>
      <ModalBody>
         <div className="review-wrapper">
            <div className={classNames({ "d-none": step == 2 })}>
               <div className="venue-info card">
                  <div className="col-12 col-sm-4 venue-img" style={{ backgroundImage: `url(${reservation.picture})` }}>
                  </div>
                  <div className="col-12 col-sm-8 px-0 pl-sm-px24">
                     <div className="venue-name">{reservation.venueName}</div>
                     <div className="venue-address">{reservation.address}</div>
                     <div className="reservation-date">{`${moment(reservation.startTime).format("dddd, MMM D")}, ${formatReservationTime(reservation.startTime, reservation.endTime)}`}</div>
                  </div>
               </div>
               <div className="rating-wrapper">
                  {ratingSystem.map((rating, index) => <div className="rating" key={index} onMouseEnter={() => setHoveredGrade(index + 1)} onMouseLeave={() => setHoveredGrade(0)} onClick={() => formMethods.setValue("stars", hoveredGrade)}>
                     <div className="star-wrapper">
                        <Icons.Star width={48} height={48} className={classNames({ "tc-icon-star": index < Math.max(hoveredGrade, Math.round(ratingValues.stars ?? 0)), "tc-icon-active": index >= Math.max(hoveredGrade, Math.round(ratingValues.stars ?? 0)) })} />
                     </div>
                     <div className="caption">{rating.text}</div>
                  </div>)}
               </div>
               <div className="comment-wrapper">
                  <InputFL control={formMethods.control} name="text" placeholder="Review" type="textarea" />
               </div>
               <div className={classNames("message-host-wrapper", { "d-none": ratingValues.stars == 0 || ratingValues.stars > 2 })}>
                  <InputFL control={formMethods.control} name="messageToSupport" placeholder="Message to support" type="textarea" />
               </div>
               <div className="sender-wrapper">
                  <Controller render={({ field }) =>
                     <>
                        <div className="caption">Submit review as:</div>
                        {
                           reviewerNameOptions.map(reviewerName => <CustomInput type="radio" name="name" key={reviewerName.id} id={reviewerName.id} defaultChecked={field.value == reviewerName.value} onChange={field.onChange} value={reviewerName.value} label={reviewerName.value} />)
                        }
                     </>
                  }
                     name="name"
                     control={formMethods.control} />
               </div>
               <div className="btn-wrapper">
                  <span id="leave-review-id">
                     <SpinningButton size="lg" color="primary" onClick={() => save()} isLoading={isLoading} disabled={isBtnDisabled()}>
                        Leave Review
                     </SpinningButton>
                  </span>
               </div>
            </div>
            <div className={classNames({ "d-none": step == 1 })}>
               <div className="preview-title">This is what your review will look like</div>
               {!!previewModel && <Review review={previewModel} className="mb-px16 mb-sm-px40" />}
               <div className="btn-wrapper">
                  <Button color="secondary" size="lg" onClick={() => moveToStep(1)}>Edit Review</Button>
                  <Button color="primary" size="lg" onClick={() => onClosePopup()}>Close</Button>
               </div>
            </div>
         </div>

      </ModalBody>
      {isBtnDisabled() && <UncontrolledTooltip target="leave-review-id">
         Please select rating
      </UncontrolledTooltip>}
   </Modal>);
}

export default ReviewModal;